import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton
} from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import AuthContext from "flexbiz-core/components/auth/Context";
import { formatCurrency } from "flexbiz-core/utils";

// Icons
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import PersonIcon from '@mui/icons-material/Person';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CancelIcon from '@mui/icons-material/Cancel';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'; // Icon cho Hoa hồng

export default function OrderDetailPage() {
  const history = useHistory();
  const { id } = useParams(); 
  const context = useContext(AuthContext);

  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // State cho việc Hủy đơn
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // ================= TẢI DỮ LIỆU ĐƠN HÀNG =================
  useEffect(() => {
    const fetchOrderDetail = async () => {
      try {
        const response = await context.apis.asyncGetList(
          context.userInfo.token,
          "nhadat_moigioi_ban",
          {
            condition: { _id: id },
            limit: 1
          }
        );

        const data = response?.data || response || [];
        if (data.length > 0) {
          setOrderData(data[0]);
        } else {
          context.alert("Không tìm thấy thông tin đơn hàng!",()=>{
            history.goBack();
          });
          
        }
      } catch (error) {
        console.error("Lỗi khi tải chi tiết đơn hàng:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrderDetail();
    }
  }, [id, context, history]);

  // ================= XỬ LÝ HỦY ĐƠN HÀNG =================
  const handleConfirmCancel = async () => {
    setIsCancelling(true);
    try {
      const payload = {
        _id: orderData._id,
        trang_thai: "9" 
      };

      const response = await context.apis.asyncPostList(
        context.userInfo.token,
        "nhadat_moigioi_ban",
        payload
      );

      const apiError = response?.error || response?.data?.error;
      
      if (apiError) {
        context.alert("Lỗi hủy đơn: " + apiError);
        return;
      }

      setOrderData((prev) => ({
        ...prev,
        trang_thai: "9",
        ten_trang_thai: "Đã hủy",
        color: "#f44336" 
      }));
      setCancelDialogOpen(false);
      
    } catch (error) {
      console.error("Lỗi khi hủy đơn:", error);
      const networkError = error.response?.data?.error || error.message || "Lỗi hệ thống!";
      context.alert("Không thể hủy đơn: " + networkError);
    } finally {
      setIsCancelling(false);
    }
  };

  // ================= UTILS =================
  const formatDate = (isoString) => {
    if (!isoString) return '---';
    const date = new Date(isoString);
    return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // ================= RENDER =================
  return (
    <Box sx={{ 
      height: '100dvh',           
      display: 'flex', 
      flexDirection: 'column',   
      backgroundColor: '#f4f6f8',
      overflow: 'hidden'         
    }}>
      
      {/* 1. HEADER */}
      <AppBar position="static" elevation={0} sx={{ flexShrink: 0, bgcolor: 'white', color: 'text.primary', borderBottom: '1px solid #e0e0e0',pb:1.5,paddingTop: "max(16px,env(safe-area-inset-top))" }}>
        <Toolbar sx={{ px: 1 }}>
          <IconButton onClick={() => history.goBack()} edge="start">
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1, textAlign: 'center', pr: 4 }}>
            Chi tiết giao dịch
          </Typography>
        </Toolbar>
      </AppBar>

      {/* 2. CONTENT AREA */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
        {loading ? (
          <Box>
            <Skeleton variant="rounded" height={100} sx={{ mb: 2, borderRadius: 2 }} />
            <Skeleton variant="rounded" height={200} sx={{ mb: 2, borderRadius: 2 }} />
            <Skeleton variant="rounded" height={100} sx={{ mb: 2, borderRadius: 2 }} />
            <Skeleton variant="rounded" height={150} sx={{ mb: 2, borderRadius: 2 }} />
          </Box>
        ) : orderData ? (
          <Box sx={{ pb: 10 }}> 
            
            {/* THẺ 1: TRẠNG THÁI & THỜI GIAN */}
            <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Mã đơn hệ thống:</Typography>
                  <Typography variant="caption" sx={{ bgcolor: '#eee', px: 1, py: 0.5, borderRadius: 1 }}>
                    ...{orderData._id?.slice(-6).toUpperCase()}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1" fontWeight="bold">Trạng thái hiện tại:</Typography>
                  <Chip 
                    label={orderData.ten_trang_thai || orderData.trang_thai} 
                    sx={{ 
                      bgcolor: orderData.color || '#9e9e9e', 
                      color: 'white', 
                      fontWeight: 'bold' 
                    }} 
                  />
                </Box>

                <Divider sx={{ mb: 1.5, borderStyle: 'dashed' }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                  <CalendarTodayIcon sx={{ fontSize: 16, mr: 1 }} />
                  <Typography variant="body2">Ngày lên đơn: {formatDate(orderData.date_created)}</Typography>
                </Box>
              </CardContent>
            </Card>

            {/* THẺ 2: THÔNG TIN SẢN PHẨM & TÀI CHÍNH */}
            <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', borderTop: '4px solid #1976d2' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MapsHomeWorkIcon fontSize="small" sx={{ mr: 1 }} /> Thông tin sản phẩm
                </Typography>
                
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {orderData.ma_nd}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {orderData.ten_nd && `Tên: ${orderData.ten_nd} | `} Tờ: {orderData.so_to || '---'} | Thửa: {orderData.so_thua || '---'}
                </Typography>

                <Grid container spacing={1.5} sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 2 }}>
                  <Grid item size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Giá bán niêm yết:</Typography>
                      <Typography variant="body2" fontWeight="500">{formatCurrency(orderData.gia_ban)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Giá bán đề nghị:</Typography>
                      <Typography variant="body2" fontWeight="500">{formatCurrency(orderData.gia_ban_de_nghi)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item size={{ xs: 12 }}>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Typography variant="body1" fontWeight="bold">Giá bán thực tế:</Typography>
                      <Typography variant="h6" fontWeight="bold" color="#d32f2f">
                        {formatCurrency(orderData.gia_ban_thuc)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* THẺ 3: THU NHẬP CỦA MÔI GIỚI (HOA HỒNG) */}
            <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 4px 12px rgba(255, 152, 0, 0.15)', borderTop: '4px solid #ff9800' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <AccountBalanceWalletIcon fontSize="small" sx={{ mr: 1, color: '#ff9800' }} /> Thu nhập dự kiến
                </Typography>

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  p: 1.5, 
                  bgcolor: '#fff8e1', 
                  borderRadius: 2, 
                  border: '1px dashed #ffb300' 
                }}>
                  <Typography variant="body2" fontWeight="bold" color="#f57c00">
                    Hoa hồng trực tiếp:
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="#e65100">
                    {formatCurrency(orderData.hoa_hong || 0)}
                  </Typography>
                </Box>
                
                {/* Hiển thị thêm tỷ lệ phần trăm nếu backend có trả về */}
                {orderData.ty_le_hh && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right', fontStyle: 'italic' }}>
                    (Tỷ lệ áp dụng: {orderData.ty_le_hh}%)
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* THẺ 4: THÔNG TIN KHÁCH HÀNG */}
            <Card sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', borderTop: '4px solid #4caf50' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon fontSize="small" sx={{ mr: 1 }} /> Thông tin khách hàng
                </Typography>

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Họ và tên</Typography>
                  <Typography variant="body1" fontWeight="bold">{orderData.ten_nguoi_mua}</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', mb: 1.5, alignItems: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">Số điện thoại</Typography>
                    <Typography variant="body1" fontWeight="500">{orderData.dien_thoai || '---'}</Typography>
                  </Box>
                  <IconButton 
                    component="a" 
                    href={`tel:${orderData.dien_thoai}`}
                    sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', width: 40, height: 40 }}
                  >
                    <PhoneIphoneIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Số CCCD</Typography>
                  <Typography variant="body2">{orderData.so_cccd || '---'}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">Địa chỉ</Typography>
                  <Typography variant="body2">{orderData.dia_chi || '---'}</Typography>
                </Box>

              </CardContent>
            </Card>
            
          </Box>
        ) : null}
      </Box>

      {/* 3. THANH ĐIỀU HƯỚNG BOTTOM (CHỨA NÚT HỦY) */}
      {orderData && orderData.trang_thai === "0" && (
        <Box sx={{ 
          flexShrink: 0, 
          p: 2, 
          bgcolor: 'white', 
          borderTop: '1px solid #e0e0e0', 
          zIndex: 1000 
        }}>
          <Button 
            fullWidth
            variant="outlined" 
            color="error" 
            size="large"
            startIcon={<CancelIcon />}
            onClick={() => setCancelDialogOpen(true)}
            sx={{ fontWeight: 'bold', borderRadius: 2 }}
          >
            Hủy đơn hàng này
          </Button>
        </Box>
      )}

      {/* ================= DIALOG XÁC NHẬN HỦY ================= */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !isCancelling && setCancelDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, m: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
          Xác nhận hủy đơn
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có chắc chắn muốn hủy giao dịch cho lô <b>{orderData?.ma_nd}</b> không? 
            Thao tác này không thể hoàn tác và lô đất sẽ được mở lại trên rổ hàng chung.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={() => setCancelDialogOpen(false)} 
            disabled={isCancelling}
            sx={{ color: 'text.secondary', fontWeight: 'bold' }}
          >
            Đóng lại
          </Button>
          <Button 
            onClick={handleConfirmCancel} 
            color="error" 
            variant="contained" 
            autoFocus
            disabled={isCancelling}
            startIcon={isCancelling && <CircularProgress size={16} color="inherit" />}
            sx={{ borderRadius: 2, fontWeight: 'bold' }}
          >
            {isCancelling ? 'Đang hủy...' : 'Đồng ý Hủy'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}